import { QueryStatus } from '@tanstack/react-query';
import React, { useMemo } from 'react';

import { Button } from '@/components/Button';
import { Entitled } from '@/components/Entitled';
import { Table } from '@/components/table/Table';
import { createClearFiltersButton } from '@/components/ui/utils/createClearFiltersButton';
import {
  getSeedColumns,
  getSeedColumnsConfig,
  getSeedTableConfig,
} from '@/sections/seeds/config';
import { PRESEED_TABLE_CONFIG } from '@/sections/seeds/config/preseed/columns';
import {
  getSeedTypeConfig,
  SeedTypeConfig,
} from '@/sections/seeds/config/seedTypeConfigs';
import { useSeedColumns, useSeedTable } from '@/sections/seeds/hooks';
import { SeedType } from '@/sections/seeds/types';
import { useGlobalState } from '@/state/global.state';
import { Seed } from '@/types';

/**
 * Seeds table component props
 *
 * Works with any seed type (domain, ip, webapplication, preseed)
 * through configuration-driven rendering.
 */
export interface SeedsTableProps {
  /** Current seed type filter */
  seedType: SeedType;
  /** Seed data to display */
  data?: Seed[];
  /** Type-specific configuration (optional, will be looked up from seedType if not provided) */
  config?: SeedTypeConfig;
  /** Loading/error status from API */
  status: QueryStatus;
  /** API error if any */
  error: Error | null;
  /** Pagination: fetch next page */
  fetchNextPage: () => void;
  /** Query: is fetching data (prevents empty state flash) */
  isFetching?: boolean;
  /** Pagination: fetching next page indicator */
  isFetchingNextPage: boolean;
  /** Pagination: has more pages */
  hasNextPage: boolean;
  /** Sort configuration */
  sort?: {
    orderBy: string; // Changed from keyof Seed to string for preseed compatibility
    descending: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSort: (sort: any) => void;
  };
  /** Selected rows state */
  selectedRows: string[];
  /** Update selected rows */
  onSelectionChange: (rows: string[]) => void;
  /** Keyboard action handler */
  onKeyboardAction: (code: string, data: Seed[]) => void;
  /** Callback for web crawl action */
  onWebCrawl: (seedKey: string) => void;
  /** Callback for comment action (hidden when undefined) */
  onCommentClick?: (seed: Seed) => void;
  /** Empty state: true if no seeds exist at all */
  isAllEmpty?: boolean;
  /** Callback to reset all filters */
  onResetFilters?: () => void;
  /** Callback to open add seed modal */
  onAddSeed?: () => void;
}

/**
 * Seeds table with sortable columns and row actions
 *
 * **Unified Component**: Works with any seed type (domain, ip, webapplication, preseed)
 * through configuration-driven rendering. Eliminates 329 lines of duplicate code
 * by unifying all seed types into a single table component.
 *
 * **Configuration-Driven**: Uses SeedTypeConfig to determine:
 * - Available columns and default visibility
 * - Row action menu items (config-driven, not hardcoded)
 * - Empty state messages
 * - Sortable fields
 *
 * Displays seeds in a data table with:
 * - Type-specific column configurations (domain, IP, web application, preseed)
 * - Sortable columns with persistent state
 * - Row selection with keyboard shortcuts
 * - Row action menu (approve, reject, scan, comment, etc.)
 * - Pagination support
 * - Empty state handling
 *
 * @example
 * ```tsx
 * // For domain/ip/webapplication seeds (backward compatible)
 * <SeedsTable
 *   seedType="domain"
 *   data={seeds}
 *   status={status}
 *   error={error}
 *   fetchNextPage={fetchNextPage}
 *   isFetchingNextPage={isFetchingNextPage}
 *   hasNextPage={hasNextPage}
 *   sort={sort}
 *   selectedRows={selectedRows}
 *   onSelectionChange={setSelectedRows}
 *   onKeyboardAction={handleKeyboardAction}
 *   onWebCrawl={(key) => modal.selectUserAgent.onAssetKeysChange([key])}
 *   onCommentClick={(seed) => {
 *     setSeedForComment(seed);
 *     modalState.comment.open();
 *   }}
 * />
 *
 * // For preseed seeds (new in Phase 4)
 * <SeedsTable
 *   seedType="preseed"
 *   data={preseedSeeds}
 *   config={getSeedTypeConfig('preseed')}
 *   {...otherProps}
 * />
 * ```
 */
export const SeedsTable: React.FC<SeedsTableProps> = ({
  seedType,
  data,
  config: providedConfig,
  status,
  error,
  fetchNextPage,
  isFetching,
  isFetchingNextPage,
  hasNextPage,
  sort,
  selectedRows,
  onSelectionChange,
  onKeyboardAction,
  onWebCrawl,
  onCommentClick,
  isAllEmpty = false,
  onResetFilters,
  onAddSeed,
}) => {
  const { drawerState } = useGlobalState();

  // Get configuration for this seed type (use provided config or look up)
  const config = providedConfig || getSeedTypeConfig(seedType);

  // Get row height from table config (preseed has its own config, others use standard)
  const rowHeight = useMemo(() => {
    if (seedType === 'preseed') {
      return PRESEED_TABLE_CONFIG.defaultRowHeight;
    }
    return getSeedTableConfig(seedType).defaultRowHeight;
  }, [seedType]);

  // Get type-specific sortable columns configuration
  const sortableColumnsConfig = useMemo(
    () => getSeedColumnsConfig(seedType),
    [seedType]
  );

  // Get column configuration for this seed type (unified for all types)
  // Key versioned to v2 to reset users to new defaults (tags not shown by default)
  const { activeColumns, setActiveColumns, columns } = useSeedColumns({
    key: `seeds_${seedType}_v2`,
    defaultColumns: getSeedColumns(seedType),
    defaultConfig: sortableColumnsConfig,
  });

  // Row actions configuration
  const { getRowActions } = useSeedTable({
    seedType,
    onWebCrawl,
    onCommentClick,
  });

  return (
    <Table
      key={seedType}
      rowHeight={rowHeight}
      tableClassName="w-full [&>table]:table-fixed [&>table]:w-full [&_th:last-child]:sticky [&_th:last-child]:right-0 [&_td:last-child]:sticky [&_td:last-child]:right-0"
      selection={{
        value: selectedRows,
        onChange: onSelectionChange,
        selectAll: true,
      }}
      sortableColumns={{
        activeColumns,
        setActiveColumns,
        defaultConfig: sortableColumnsConfig,
      }}
      keyboardActions={{
        codes: ['KeyA', 'KeyR', 'KeyD', 'KeyF'],
        altKey: true,
        onKeyboardAction,
      }}
      keyboardNavigation={true}
      onRowClick={seed => {
        if (seedType === 'preseed') {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          drawerState.handleOpenPreseedDrawer(seed as any);
        } else {
          drawerState.handleOpenSeedDrawer(seed);
        }
      }}
      sort={
        sort && {
          orderBy: sort.orderBy as keyof Seed,
          descending: sort.descending,
          onSort: sort.onSort,
        }
      }
      name="seeds"
      columns={columns}
      rowActions={getRowActions}
      data={data || []}
      error={error}
      status={status}
      isFetching={isFetching}
      fetchNextPage={fetchNextPage}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      noData={{
        icon: config.emptyState.icon,
        title: isAllEmpty
          ? config.emptyState.title
          : 'No Seeds match your current filter',
        description: isAllEmpty ? config.emptyState.description : undefined,
        action: isAllEmpty ? (
          onAddSeed ? (
            <Entitled entitlement="write_assets">
              <Button
                label="Get Started"
                onClick={onAddSeed}
                styleType="primary"
                size="sm"
              />
            </Entitled>
          ) : undefined
        ) : onResetFilters ? (
          createClearFiltersButton({
            hasActiveFilters: true,
            onClearFilters: onResetFilters,
          })
        ) : undefined,
      }}
    />
  );
};
